import { errorLogger } from "@/Common/helpers/logger";
import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import {
	ICreatePayPlanResponse,
	IPayPlanInput,
	IInfoInputPayPlan,
	IIdInputPayPlan,
	IPayPlanModelInputPayPlan,
	IDeleteResponse,
	IUpdatesAvailablePayPlanResponse,
} from "@/PayPlans/types/api";
import { apiInstance, gql } from "@/services/httpAdapter";

// Fragments
import {
	CallActivityPerformanceFragment,
	CommissionTiersFragment,
	ELRAndHROBonusFragment,
	InputValueFragment,
	LongevityFragment,
	MetricFragment,
	TopPerformerBonusFragment,
} from "@PayPlans/api/fragments/commons";

import { CustomerSatisfactionFragment } from "@PayPlans/api/fragments/salesAssociates";
import {
	AdvisorDefaultFragment,
	FollowUpPerformanceBonusFragment,
	RetentionFragment,
	HolidayPayFragment,
} from "@PayPlans/api/fragments/service/advisors";

// Create sales departmental pay plans
export async function createAdvisorPayPlan({ locationId, position }: IPayPlanInput) {
	const query = gql`
		mutation createAdvisorPayPlan($locationId: String!, $position: String!) {
			createAdvisorPayPlan(locationId: $locationId, position: $position) {
				name
				createdAt
			}
		}
	`;

	const variables = { locationId, position };

	try {
		const response: GraphqlQueryResponse<"createAdvisorPayPlan", ICreatePayPlanResponse> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.createAdvisorPayPlan ?? null;
	} catch (error) {
		return error;
	}
}
// Update available sales departmental pay plans
export async function updateAdvisorAvailablePayPlan({ locationId, information, position }: IInfoInputPayPlan) {
	const { _id, updatedBy, available } = information;
	const query = gql`
		mutation updateAdvisorAvailablePayPlan($locationId: String!, $input: UpdateAvailablePayPlanInput!, $position: String!) {
			updateAdvisorAvailablePayPlan(locationId: $locationId, input: $input, position: $position) {
				_id
				locationId
				name
				position
				available
			}
		}
	`;

	const variables = {
		locationId,
		position,
		input: {
			_id,
			updatedBy,
			available,
		},
	};

	try {
		const response: GraphqlQueryResponse<"updateAdvisorAvailablePayPlan", IUpdatesAvailablePayPlanResponse> = await apiInstance.post(
			"/graphql",
			{ query, variables }
		);
		return response.data.data?.updateAdvisorAvailablePayPlan;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}
// Add new sales departmental pay plans
export async function cloneAdvisorPayPlan({ locationId, information, actualPosition }: IInfoInputPayPlan) {
	const { _id, createdBy, position } = information;
	const query = gql`
		mutation cloneAdvisorPayPlan($locationId: String!, $input: ClonePayPlan!, $position: String!) {
			cloneAdvisorPayPlan(locationId: $locationId, input: $input, position: $position) {
				_id
				locationId
				name
				position
				default {
					...AdvisorDefault
				}
				followUpPerformanceBonus {
					...FollowUpPerformanceBonus
				}
				callActivityPerformance {
					...CallActivityPerformance
				}
				customerSatisfaction {
					...CustomerSatisfaction
				}
				longevity {
					...Longevity
				}
				retentionPay {
					...Retention
				}
				holidayPay {
					...HolidayPay
				}
				topAdvisorsBonus {
					...TopPerformerBonus
				}
				elr {
					...ELRAndHROBonus
				}
				hro {
					...ELRAndHROBonus
				}
				locked
				available
				createdAt
				createdBy {
					_id
					fullName
				}
				updatedAt
				updatedBy {
					_id
					fullName
				}
			}
		}
		${AdvisorDefaultFragment}
		${CallActivityPerformanceFragment}
		${CommissionTiersFragment}
		${CustomerSatisfactionFragment}
		${FollowUpPerformanceBonusFragment}
		${InputValueFragment}
		${LongevityFragment}
		${MetricFragment}
		${TopPerformerBonusFragment}
		${ELRAndHROBonusFragment}
		${RetentionFragment}
		${HolidayPayFragment}
	`;

	const variables = {
		locationId,
		input: {
			payPlanId: _id,
			createdBy,
			position,
		},
		position: actualPosition,
	};

	try {
		//Replace Object with PayPlan type
		const response: GraphqlQueryResponse<"cloneAdvisorPayPlan", Object> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.cloneAdvisorPayPlan ?? null;
	} catch (error) {
		return error;
	}
}
// Delete selected sales departmental pay plans
export async function deleteAdvisorPayPlan({ locationId, _id, position }: IIdInputPayPlan) {
	const query = gql`
		mutation deleteAdvisorPayPlan($locationId: String!, $id: String!, $position: String!) {
			deleteAdvisorPayPlan(id: $id, locationId: $locationId, position: $position) {
				_id
				locationId
			}
		}
	`;

	const variables = {
		id: _id,
		position,
		locationId,
	};

	try {
		const response: GraphqlQueryResponse<"deleteAdvisorPayPlan", IDeleteResponse> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.deleteAdvisorPayPlan ?? null;
	} catch (error) {
		return error;
	}
}
// Update selected sales departmental pay plan
export async function updateAdvisorPayPlan({ locationId, payPlan, position }: IPayPlanModelInputPayPlan) {
	const query = gql`
		mutation updateAdvisorPayPlan($locationId: String!, $input: UpdateAdvisorPayPlan!, $position: String!) {
			updateAdvisorPayPlan(input: $input, locationId: $locationId, position: $position) {
				_id
				locationId
				name
				position
				default {
					...AdvisorDefault
				}
				followUpPerformanceBonus {
					...FollowUpPerformanceBonus
				}
				callActivityPerformance {
					...CallActivityPerformance
				}
				customerSatisfaction {
					...CustomerSatisfaction
				}
				longevity {
					...Longevity
				}
				retentionPay {
					...Retention
				}
				holidayPay {
					...HolidayPay
				}
				topAdvisorsBonus {
					...TopPerformerBonus
				}
				elr {
					...ELRAndHROBonus
				}
				hro {
					...ELRAndHROBonus
				}
				locked
				available
				createdAt
				createdBy {
					_id
					fullName
				}
				updatedAt
				updatedBy {
					_id
					fullName
				}
			}
		}
		${AdvisorDefaultFragment}
		${CallActivityPerformanceFragment}
		${CommissionTiersFragment}
		${CustomerSatisfactionFragment}
		${FollowUpPerformanceBonusFragment}
		${InputValueFragment}
		${LongevityFragment}
		${MetricFragment}
		${TopPerformerBonusFragment}
		${ELRAndHROBonusFragment}
		${RetentionFragment}
		${HolidayPayFragment}
	`;

	const variables = {
		input: payPlan,
		locationId,
		position,
	};

	try {
		//Replace Object with PayPlan type
		const response: GraphqlQueryResponse<"updateAdvisorPayPlan", Object> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.updateAdvisorPayPlan ?? null;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}
// save new selected sales departmental pay plan
export async function saveNewAdvisorPayPlan({ locationId, payPlan, position }: IPayPlanModelInputPayPlan) {
	const query = gql`
		mutation saveNewAdvisorPayPlan($locationId: String!, $input: SaveAdvisorPayPlan!, $position: String!) {
			saveNewAdvisorPayPlan(locationId: $locationId, input: $input, position: $position) {
				_id
				locationId
				name
				position
				default {
					...AdvisorDefault
				}
				followUpPerformanceBonus {
					...FollowUpPerformanceBonus
				}
				callActivityPerformance {
					...CallActivityPerformance
				}
				customerSatisfaction {
					...CustomerSatisfaction
				}
				longevity {
					...Longevity
				}
				retentionPay {
					...Retention
				}
				holidayPay {
					...HolidayPay
				}
				topAdvisorsBonus {
					...TopPerformerBonus
				}
				elr {
					...ELRAndHROBonus
				}
				hro {
					...ELRAndHROBonus
				}
				locked
				available
				createdAt
				createdBy {
					_id
					fullName
				}
				updatedAt
				updatedBy {
					_id
					fullName
				}
			}
		}
		${AdvisorDefaultFragment}
		${CallActivityPerformanceFragment}
		${CommissionTiersFragment}
		${CustomerSatisfactionFragment}
		${FollowUpPerformanceBonusFragment}
		${InputValueFragment}
		${LongevityFragment}
		${MetricFragment}
		${TopPerformerBonusFragment}
		${ELRAndHROBonusFragment}
		${RetentionFragment}
		${HolidayPayFragment}
	`;

	const variables = {
		locationId,
		position,
		input: payPlan,
	};

	try {
		//Replace Object with PayPlan type
		const response: GraphqlQueryResponse<"saveNewAdvisorPayPlan", Object> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.saveNewAdvisorPayPlan;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}
